import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, FileText, CheckCircle, AlertCircle, X,
  Play, Trash2, RotateCw, Zap,
  Eye, BookOpen, FolderOpen, Library
} from 'lucide-react';
import './UploadIndexation.css';
import './accessibility.css';
import { apiJSON, apiUpload, createSSEConnection, apiFetch } from '../config/api';
import notificationService, { handleApiError } from '../utils/notificationService';
import IngestionProgress from './IngestionProgress';
import IngestionNotification from './IngestionNotification';
import Button from './ui/Button';

// Types minimaux (P1 : suppression de la directive ts-nocheck). Formes souples,
// alimentées par des uploads et un flux SSE dont le contrat n'est pas garanti.
interface UploadedFile {
  id: number;
  file: File;
  name: string;
  size: number;
  status: string;
  progress: number;
  message: string;
  // Identifiant renvoyé par /api/import : c'est la clé du flux SSE de
  // progression, distincte de `id` qui n'existe que côté navigateur.
  bookId?: string;
}

interface AppError {
  id: number;
  message: string;
  timestamp: Date;
}

interface UploadNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  stage?: string;
  progress?: number;
  timestamp: Date;
  dismissed: boolean;
}

interface SSEEventData {
  book_id?: string;
  stage?: string;
  status?: string;
  percent?: number;
  message_fr?: string;
}

const UploadIndexation: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState<AppError[]>([]);
  const [notifications, setNotifications] = useState<UploadNotification[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // `createSSEConnection` rend une poignée ({ close, readyState }) et non
  // l'EventSource elle-même, pour survivre aux reconnexions.
  const sseConnections = useRef<Map<number, { close: () => void }>>(new Map());
  // Toujours la dernière version de `handleSSEEvent` (cf. processFile).
  const handleSSEEventRef = useRef<(fileId: number, data: SSEEventData) => void>(
    () => {}
  );

  // Indique si tous les fichiers ajoutés ont été traités avec succès et qu'il y en a au moins un.
  const allCompleted =
    uploadedFiles.length > 0 &&
    uploadedFiles.every((f) => f.status === 'completed');

  // Nettoyage des connexions SSE au démontage du composant
  useEffect(() => {
    const map = sseConnections.current; // capture la ref à l'instant T
    return () => {
      try {
        for (const es of map.values()) {
          es?.close?.();
        }
        map.clear();
      } catch (e) {
        console.warn("[SSE cleanup] erreur", e);
      }
    };
  }, []); // pas de dépendances

  const steps = [
    {
      label: 'Sélection des fichiers',
      description: 'Glissez-déposez ou sélectionnez vos livres (PDF, EPUB, TXT)',
      icon: <Upload size={20} />
    },
    {
      label: 'Analyse et détection',
      description: 'Détection du format et vérification des doublons',
      icon: <Eye size={20} />
    },
    {
      label: 'Extraction et OCR',
      description: 'Extraction du texte et OCR si nécessaire',
      icon: <FileText size={20} />
    },
    {
      label: 'Nettoyage et segmentation',
      description: 'Nettoyage du texte et découpage en segments',
      icon: <FolderOpen size={20} />
    },
    {
      label: 'Indexation vectorielle',
      description: 'Génération des embeddings et indexation',
      icon: <BookOpen size={20} />
    },
    {
      label: 'Terminé',
      description: 'Vos livres sont prêts pour la recherche et l\'IA',
      icon: <CheckCircle size={20} />
    }
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    handleFiles(files);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
    const files = Array.from(event.dataTransfer.files);
    handleFiles(files);
  };

  const handleFiles = (files: File[]) => {
    const validFiles = files.filter(file => {
      const validTypes = ['.pdf', '.epub', '.txt']; // Harmonisé avec backend
      const extension = '.' + (file.name.split('.').pop() || '').toLowerCase();
      
      // Validation extension
      if (!validTypes.includes(extension)) {
        addError(`Type non supporté: ${file.name} (${extension}). Types autorisés: PDF, EPUB, TXT`);
        return false;
      }
      
      // Validation taille (300MB max)
      const maxSize = 300 * 1024 * 1024;
      if (file.size > maxSize) {
        addError(`Fichier trop volumineux: ${file.name} (${formatFileSize(file.size)}). Maximum: 300MB`);
        return false;
      }
      
      return true;
    });

    if (validFiles.length !== files.length) {
      const invalidCount = files.length - validFiles.length;
      addError(`${invalidCount} fichier(s) non supporté(s) ignoré(s)`);
    }

    const newFiles = validFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      status: 'pending', // pending, uploading, processing, completed, error
      progress: 0,
      message: 'En attente...'
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
  };

  const addError = (message: string) => {
    setErrors(prev => [...prev, { id: Date.now(), message, timestamp: new Date() }]);
  };

  const removeError = (errorId: number) => {
    setErrors(prev => prev.filter(err => err.id !== errorId));
  };

  // Fonction pour ajouter une notification
  const addNotification = (
    type: string,
    title: string,
    message: string,
    stage?: string,
    progress?: number,
  ) => {
    const notification: UploadNotification = {
      id: Date.now(),
      type,
      title,
      message,
      timestamp: new Date(),
      dismissed: false,
      // exactOptionalPropertyTypes : n'inclure ces clés que si définies.
      ...(stage !== undefined ? { stage } : {}),
      ...(progress !== undefined ? { progress } : {}),
    };
    setNotifications(prev => [...prev, notification]);
  };

  // Fonction pour fermer une notification
  const dismissNotification = (id: number) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, dismissed: true } : n)
    );
  };

  // Événements SSE conservés par fichier : ils alimentent le détail par étapes
  // sans qu'il ait besoin d'ouvrir sa propre connexion.
  const [evenementsParFichier, setEvenementsParFichier] = useState<Record<number, SSEEventData[]>>({});

  // Rattacher au fichier l'identifiant serveur, seul lien avec le flux SSE.
  const attacherBookId = (fileId: number, bookId: string) => {
    setUploadedFiles(prev =>
      prev.map(file => (file.id === fileId ? { ...file, bookId } : file))
    );
  };

  // Fonction pour mettre à jour le statut d'un fichier
  const updateFileStatus = (
    fileId: number,
    status: string,
    message = '',
    progress = 0,
  ) => {
    setUploadedFiles(prev =>
      prev.map(file =>
        file.id === fileId
          ? { ...file, status, message, progress }
          : file
      )
    );
  };

  // Fonction pour traiter un fichier
  const processFile = useCallback(async (fileItem: UploadedFile) => {
    try {
      updateFileStatus(fileItem.id, 'uploading', 'Upload en cours...', 10);

      const formData = new FormData();
      formData.append('file', fileItem.file);

      // Utiliser apiUpload pour une meilleure gestion d'erreurs
      const result = await apiUpload('import', formData, (progress: number) => {
        updateFileStatus(fileItem.id, 'uploading', `Upload en cours... ${Math.round(progress)}%`, progress);
      });

      const bookId = result.book_id;

      if (!bookId) {
        throw new Error("Upload réussi mais aucun book_id retourné");
      }

      attacherBookId(fileItem.id, bookId);
      updateFileStatus(fileItem.id, 'processing', 'Traitement en cours...', 20);

      // Utiliser createSSEConnection pour une meilleure gestion SSE.
      // L'appel passe par une ref : `processFile` est mémoïsé avec des
      // dépendances vides et figerait sinon la version de `handleSSEEvent` du
      // premier rendu, pour toute la durée de vie du composant.
      const eventSource = createSSEConnection(
        `sse/events/${bookId}`,
        (data: SSEEventData) => {
          // Le serveur diffuse chaque événement à tous les abonnés : sans ce
          // filtre, deux imports simultanés s'attribueraient mutuellement leur
          // progression.
          if (data?.book_id && data.book_id !== bookId) return;
          handleSSEEventRef.current(fileItem.id, data);
        },
        (error: unknown) => {
          console.warn('SSE Error:', error);
          notificationService.showToast('Problème de connexion temps réel', 'warning');
        }
      );
      
      // Stocker la connexion pour pouvoir la fermer plus tard
      sseConnections.current.set(fileItem.id, eventSource);

              notificationService.showToast(`Suivi temps réel actif pour ${fileItem.name}`, 'success', 2000);

    } catch (error: any) {
      updateFileStatus(fileItem.id, 'error', `Erreur: ${error?.message ?? error}`);
      addError(`Erreur pour ${fileItem.name}: ${error?.message ?? error}`);
      handleApiError(error, `Traitement ${fileItem.name}`);
    }
  }, []);

  // Fonction pour relancer le traitement d'un fichier
  const retryFile = useCallback((fileId: number) => {
    // TODO: remets le fichier en file d'attente, relance l'upload/SSE, etc.
    console.log("[retryFile] relancer le traitement pour", fileId);
    
    // Réinitialiser le statut du fichier
    updateFileStatus(fileId, 'pending', 'En attente de retry...', 0);
    
    // Trouver le fichier et relancer le traitement
    const fileItem = uploadedFiles.find(f => f.id === fileId);
    if (fileItem) {
      setTimeout(() => {
        processFile(fileItem);
      }, 1000);
    }
    
    // Exemple d'idée pour plus tard:
    // requeueFile(fileId);
    // startProcessing(fileId);
  }, [uploadedFiles, processFile]);

  // Fonction pour démarrer le traitement de tous les fichiers en attente
  const startProcessing = useCallback(async () => {
    if (uploadedFiles.length === 0) return;

    for (const fileItem of uploadedFiles) {
      if (fileItem.status === 'pending') {
        await processFile(fileItem);
      }
    }
  }, [uploadedFiles, processFile]);

  // Utilisation du service de notifications centralisé
  const checkFileStatus = async () => {
    try {
      const data = await apiJSON('books?limit=1000');
      const books = Array.isArray(data) ? data : (data.books || []);
      
      const processedFiles = uploadedFiles.filter(file =>
        books.some((book: any) => book.file_original_name === file.name)
      );
      
      if (processedFiles.length > 0) {
        notificationService.showToast(`${processedFiles.length} fichier(s) trouvé(s) dans la base de données`, 'success');
        processedFiles.forEach(file => {
          updateFileStatus(file.id, 'completed', 'Traitement terminé (vérifié)', 100);
        });
      } else {
                  notificationService.showToast('Aucun fichier traité trouvé - traitement peut être en cours', 'info');
      }
    } catch (error) {
      handleApiError(error, 'Vérification statut');
      console.error('Erreur vérification statut:', error);
    }
  };

  const testSSEConnection = async () => {
    try {
      const response = await apiFetch('sse/events/test');
      
      if (response.ok) {
        notificationService.showToast('Connexion SSE disponible', 'success');
      } else {
                  notificationService.showToast('Connexion SSE non disponible', 'warning');
      }
    } catch (error) {
      handleApiError(error, 'Test SSE');
      console.error('Erreur test SSE:', error);
    }
  };





  const handleSSEEvent = (fileId: number, data: SSEEventData) => {
    const { stage, status, percent, message_fr } = data;

    setEvenementsParFichier(prev => ({
      ...prev,
      [fileId]: [...(prev[fileId] || []), data]
    }));
    
    console.log('SSE Event reçu:', { fileId, stage, status, percent, message_fr });
    
    // Calculer la progression basée sur le stage
    let progress = percent || 0;
    if (!percent && stage) {
      const stageIndex = getStageIndex(stage);
      if (stageIndex >= 0) {
        progress = Math.round((stageIndex / (steps.length - 1)) * 100);
      }
    }
    
    // Gestion spéciale pour l'événement DONE
    if (stage === 'DONE' && status === 'success') {
      console.log('Événement DONE détecté, finalisation...');
      updateFileStatus(fileId, 'completed', 'Indexation terminée (100%)', 100);
      setActiveStep(steps.length - 1);
              notificationService.showToast(`Indexation terminée avec succès`, 'success');
      addNotification('success', 'Indexation terminée', 'Livre prêt pour la recherche et l\'IA', 'DONE', 100);
      
      // Fermer la connexion SSE après succès
      closeSSEForFile(fileId);
      return; // Sortir immédiatement
    }
    
    // Message détaillé avec progression
    const detailedMessage = message_fr ? 
      `${message_fr} (${progress}%)` : 
      `Traitement en cours... (${progress}%)`;
    
    updateFileStatus(fileId, status ?? 'processing', detailedMessage, progress);

    // Synchroniser Stepper global avec progression. Mise à jour fonctionnelle :
    // lire `activeStep` depuis la closure la figeait à sa valeur initiale.
    const stageIndex = getStageIndex(stage);
    if (stageIndex >= 0) {
      setActiveStep((precedent) => Math.max(precedent, stageIndex));
    }
    
    // Ajouter des notifications détaillées selon le type d'événement
    if (status === 'progress') {
      addNotification('progress', 'Traitement en cours', message_fr || `Étape ${stage}`, stage, progress);
    } else if (status === 'success') {
      addNotification('success', 'Étape terminée', message_fr || `Étape ${stage} terminée`, stage, progress);
    } else if (status === 'error') {
      addNotification('error', 'Erreur détectée', message_fr || `Erreur à l'étape ${stage}`, stage, progress);
      addError(`Erreur ${stage}: ${message_fr}`);
      // Une ingestion en échec n'émettra plus rien : garder le flux ouvert
      // laisserait le fichier tourner indéfiniment.
      closeSSEForFile(fileId);
    } else if (status === 'warning') {
      addNotification('warning', 'Attention', message_fr || `Avertissement à l'étape ${stage}`, stage, progress);
    } else if (status === 'decision') {
      addNotification('info', 'Décision', message_fr || `Décision à l'étape ${stage}`, stage, progress);
    }
    
    // Affichage toast selon le type d'événement
    if (status === 'decision') {
              notificationService.showToast(`${message_fr}`, 'info');
    } else if (status === 'warning') {
              notificationService.showToast(`${message_fr}`, 'warning');
    } else if (status === 'error') {
              notificationService.showToast(`Erreur ${stage}: ${message_fr}`, 'error');
    } else if (stage === 'DONE' && status === 'success') {
      // Gestion déjà faite plus haut
      console.log('Événement DONE déjà traité');
    }
  };

  // Publier la version courante pour les connexions SSE déjà ouvertes.
  handleSSEEventRef.current = handleSSEEvent;

  // Mapper stages aux index du stepper
  const getStageIndex = (stage?: string) => {
    const stageMap: Record<string, number> = {
      'RECEIVED': 0,
      'FORMAT_DETECT': 1,
      'CONVERT': 1,
      'OCR': 2,
      'EXTRACT': 2,
      'CLEAN': 3,
      'SPLIT': 3,
      'EMBEDDINGS': 4,
      'INDEX': 4,
      'CATALOG': 4,
      'DONE': 5
    };
    return stage ? (stageMap[stage] ?? -1) : -1;
  };

  // Gestion des connexions SSE par fichier (déjà déclaré plus haut)

  const closeSSEForFile = (fileId: number) => {
    const connection = sseConnections.current.get(fileId);
    if (connection) {
      connection.close();
      sseConnections.current.delete(fileId);
      console.log(`SSE fermé pour fichier ${fileId}`);
    }
  };

  const removeFile = (fileId: number) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const clearCompleted = () => {
    setUploadedFiles(prev => prev.filter(file => file.status !== 'completed'));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="upload-indexation app-page">
      <header className="app-page__header upload-indexation__header">
        <div className="app-page__titlegroup">
          <h1 className="app-page__title">Upload &amp; Indexation</h1>
          {uploadedFiles.length > 0 && (
            <span className="app-page__count">{uploadedFiles.length} fichier(s)</span>
          )}
        </div>
      </header>

      {/* Zone de drop */}
      <div
        className={`drop-zone${dragActive ? ' drag-over' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.epub,.txt"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        <Upload size={48} aria-hidden />
        <h3>Glissez vos livres ici ou cliquez pour sélectionner</h3>
        <p>Formats supportés : PDF, EPUB, TXT uniquement (max 300MB par fichier)</p>
      </div>

      {/* Pipeline d'ingestion (stepper) */}
      <div className="upload-pipeline">
        <h2 className="upload-pipeline__title">Pipeline d'ingestion</h2>
        <div className="upload-pipeline__steps">
          {steps.map((step, index) => (
            <div
              key={step.label}
              className={`upload-pipeline__step${
                index === activeStep ? ' is-active' : index < activeStep ? ' is-done' : ''
              }`}
            >
              <div className="upload-pipeline__step-icon">{step.icon}</div>
              <div>
                <p className="upload-pipeline__step-label">{step.label}</p>
                <p className="upload-pipeline__step-description">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Résumé succès / erreurs (AUD-007) */}
      {uploadedFiles.length > 0 && (() => {
        const successCount = uploadedFiles.filter(f => f.status === 'completed').length;
        const errorCount = uploadedFiles.filter(f => f.status === 'error').length;
        const pendingCount = uploadedFiles.filter(f => f.status === 'pending' || f.status === 'uploading' || f.status === 'processing').length;
        return (
          <div className={`upload-summary${errorCount > 0 ? ' has-errors' : ''}`}>
            <div className="upload-summary__counts">
              <span className="upload-summary__count upload-summary__count--success">
                <CheckCircle size={16} aria-hidden />
                {successCount} succès
              </span>
              {errorCount > 0 && (
                <span className="upload-summary__count upload-summary__count--error">
                  <AlertCircle size={16} aria-hidden />
                  {errorCount} erreur(s)
                </span>
              )}
              {pendingCount > 0 && (
                <span className="upload-summary__count upload-summary__count--pending">
                  {pendingCount} en attente / en cours
                </span>
              )}
            </div>
            {errorCount > 0 && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => uploadedFiles.filter(f => f.status === 'error').forEach(f => retryFile(f.id))}
              >
                Réessayer les échecs
              </Button>
            )}
          </div>
        );
      })()}

      {/* Bannière "tous terminés" avec CTA navigation (AUD-008 : parcours complet upload→bibliothèque) */}
      {allCompleted && (
        <div className="upload-complete">
          <div className="upload-complete__info">
            <CheckCircle size={28} className="upload-summary__count--success" aria-hidden />
            <div>
              <h3 className="upload-complete__title">
                {uploadedFiles.length === 1
                  ? 'Livre indexé avec succès'
                  : `${uploadedFiles.length} livres indexés avec succès`}
              </h3>
              <p className="upload-complete__subtitle">
                Vos documents sont prêts pour la recherche et l'IA.
              </p>
            </div>
          </div>
          <div className="upload-complete__actions">
            <Button variant="primary" size="sm" onClick={() => navigate('/')}>
              <Library size={16} aria-hidden /> Voir la bibliothèque
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setUploadedFiles([]);
                setActiveStep(0);
                setErrors([]);
              }}
            >
              Importer d'autres livres
            </Button>
          </div>
        </div>
      )}

      {/* Contrôles */}
      {uploadedFiles.length > 0 && (
        <div className="upload-controls">
          <Button
            variant="primary"
            onClick={startProcessing}
            disabled={uploadedFiles.every(f => f.status !== 'pending')}
          >
            <Play size={16} aria-hidden /> Démarrer l'ingestion
          </Button>

          <Button variant="secondary" onClick={clearCompleted}>
            <Trash2 size={16} aria-hidden /> Nettoyer terminés
          </Button>

          <Button variant="secondary" onClick={checkFileStatus}>
            <RotateCw size={16} aria-hidden /> Vérifier statut
          </Button>

          <Button variant="ghost" onClick={testSSEConnection}>
            <Zap size={16} aria-hidden /> Test SSE
          </Button>
        </div>
      )}

      {/* Composants de progression détaillée pour chaque fichier */}
      {uploadedFiles.map((fileItem) => (
        <IngestionProgress
          key={fileItem.id}
          fileItem={{
            ...fileItem,
            fileSize: formatFileSize(fileItem.file.size)
          }}
          evenements={evenementsParFichier[fileItem.id] || []}
          onClose={() => removeFile(fileItem.id)}
          onRetry={() => retryFile(fileItem.id)}
        />
      ))}

      {/* Erreurs */}
      {errors.length > 0 && (
        <div className="upload-errors">
          <h3 className="upload-errors__title">
            <AlertCircle size={20} aria-hidden />
            Erreurs
          </h3>

          {errors.map((error) => (
            <div key={error.id} className="upload-errors__item">
              <span className="upload-errors__message">{error.message}</span>
              <button
                className="upload-errors__dismiss"
                onClick={() => removeError(error.id)}
                aria-label="Supprimer cette erreur"
              >
                <X size={16} aria-hidden />
                <span className="sr-only">Supprimer</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Système de notifications d'ingestion */}
      <IngestionNotification
        notifications={notifications}
        onClose={dismissNotification}
        onClearAll={() => setNotifications([])}
      />
    </div>
  );
};

export default UploadIndexation;